import Highcharts from "highcharts";
import _ from "lodash";
import * as DateUtils from "../../../utils/date/date";
import {
    AnalysisPlusStructures,
    ComparationStructures
} from "./ComparationStructures";

export class HighchartsFormaterStrutureAdapter {
    constructor({
        FormatUnits,
        HighchartsFormatSeriesAdapter,
        AvailablePeriod
    }) {
        this.AvailablePeriod = AvailablePeriod;
        this.FormatUnits = FormatUnits;
        this.HighchartsFormatSeriesAdapter = HighchartsFormatSeriesAdapter;
    }

    _formatAlternativeStructure({
        structure,
        currentComparation,
        series,
        projection,
        budget,
        goals,
        highlightsSeries
    } = {}) {
        if (!currentComparation.active && !projection && !budget && !goals)
            return structure;
        const customStructures =
            projection || budget || goals
                ? AnalysisPlusStructures
                : ComparationStructures;
        const comparationStructure = angular.copy(
            customStructures[structure.category]
        );
        if (!comparationStructure) return structure;
        if (goals && structure?.goals) {
            comparationStructure.legend = {
                enabled: true
            };
            comparationStructure.goals = structure.goals.map((serie) => {
                return {
                    _id: serie._id,
                    type: "line",
                    color: serie.color || "goals.medium",
                    dashStyle: serie.dashStyle || "ShortDash"
                };
            });
        }
        let _series = [];
        if (structure.category === "waterfall" && goals) {
            _series = highlightsSeries;
        } else {
            _series = structure.series || series;
        }
        comparationStructure.series = _series.map((serie, index) => {
            return {
                _id: serie._id,
                color: serie.color || "cyan.medium"
            };
        });
        return comparationStructure;
    }

    /**
     * @public
     * Manipuala estrutura do higchart combinando informações custimizadas com infomrações fixas
     * @param {Object} param0
     * @returns
     */
    getStructure({
        companyId,
        structure = {},
        series = [],
        points = [],
        filters = {},
        rewriteCategories = false,
        currentComparation = {},
        options = {}
    } = {}) {
        this.rewriteCategories = rewriteCategories;
        let highlightsSeries = [];
        if (options.goals && structure.category === "waterfall")
            highlightsSeries = options?.highlightsSeries?.map((serie) => {
                return {
                    _id: serie._id,
                    name: serie.name,
                    name_nd: serie.name_nd,
                    polarity: serie.polarity,
                    unit: serie.unit
                };
            });
        const beforeCategory = angular.copy(structure.category);
        const _structure = this._formatAlternativeStructure({
            structure: angular.copy(structure),
            currentComparation,
            series: angular.copy(series),
            projection: options.projection,
            budget: options.budget,
            goals: options.goals,
            highlightsSeries: highlightsSeries
        });
        structure = this._structure({
            companyId,
            structure: _structure,
            series: series,
            points,
            filters,
            currentComparation,
            budget: options.budget,
            goals: options.goals,
            highlightsSeries,
            beforeCategory
        });
        if (!structure) return;
        return structure;
    }

    formatYAxis({ series = [], structure = {} } = {}) {
        if (!structure.yAxis || !_.get(series, "length")) return {};

        const units = {
            currency: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);
                _.set(axis, "labels.formatter", this._currencyLabelFormatter());
                return axis;
            },
            percentage: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);

                if (_.get(axis, "labels.formatter"))
                    delete axis.labels.formatter;

                _.set(axis, "labels.format", "{value:.2f}%");
                return axis;
            },
            number: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);

                if (_.get(axis, "labels.formatter"))
                    delete axis.labels.formatter;

                _.set(axis, "labels.format", "{value}");
                return axis;
            },
            int: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);

                if (_.get(axis, "labels.formatter"))
                    delete axis.labels.formatter;

                _.set(axis, "labels.format", "{value}");
                return axis;
            },
            day: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);

                if (_.get(axis, "labels.formatter"))
                    delete axis.labels.formatter;

                _.set(axis, "labels.format", "{value} dias");
                return axis;
            }
        };

        if (Array.isArray(structure.yAxis)) {
            const yAxis = [];
            structure.yAxis.forEach((_yAxis) => {
                if (_yAxis.labels.formatter === "currency") {
                    const axis = units["currency"](_yAxis);
                    yAxis.push(axis);
                } else yAxis.push(_yAxis);
            });

            return yAxis;
        }

        if (units[series[0].unit])
            return units[series[0].unit](structure.yAxis);
    }

    _currencyLabelFormatter() {
        return function () {
            function kFormatter(num) {
                if (Math.abs(num) < 1e6)
                    return (
                        Math.sign(num) * (Math.abs(num) / 1e3).toFixed(1) + "k"
                    );
                else if (Math.abs(num) >= 1e6)
                    return (
                        Math.sign(num) * (Math.abs(num) / 1e6).toFixed(1) + "M"
                    );
            }

            if (Math.abs(this.value) > 999)
                return "R$ " + kFormatter(this.value);
            return this.value.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL"
            });
        };
    }

    getSeriesToChart({ series, seriesDefault, goals, seriesGoals }) {
        if (_.get(seriesDefault, "length")) {
            const ids = seriesDefault.map((s) => s._id);
            return series.filter(
                (s) => ids.includes(s._id) || ids.includes(s?.referenceId)
            );
        }
        return series;
    }

    /**
     * @private
     *
     * @returns {Object} de configurações do gráfico
     */
    _structure({
        companyId,
        structure = {},
        series = [],
        points = [],
        filters = {},
        currentComparation = {},
        budget = false,
        goals = false,
        highlightsSeries,
        beforeCategory
    }) {
        if (beforeCategory === "waterfall" && goals) {
            if (structure?.goals?.length) {
                const seriesGoals = series.filter((s) => {
                    return s.origin === "goals";
                });
                highlightsSeries.push(...seriesGoals);
            }
            series = highlightsSeries;
        }
        if (goals && structure?.goals?.length) {
            structure.series.push(...structure?.goals);
        }
        const newSeries = this.getSeriesToChart({
            series: series,
            seriesDefault: _.get(structure, "series")
        });
        const seriesMapChart = this.HighchartsFormatSeriesAdapter.format({
            series: newSeries,
            points,
            dates: this.AvailablePeriod.getByPoints(points),
            seriesDefault: _.get(structure, "series") || [],
            type: _.get(structure, "category"),
            filters,
            currentComparation,
            budget,
            goals
        });

        if (!seriesMapChart) return;
        const categories = this.HighchartsFormatSeriesAdapter.dates;

        if (this._isSparseCategories(categories) && !currentComparation.active) {
            const tsToIdx = {};
            categories.forEach((ts, i) => { tsToIdx[parseInt(ts)] = i; });
            seriesMapChart.forEach((serie) => {
                if (!Array.isArray(serie.data)) return;
                serie.data.forEach((point) => {
                    if (point && typeof point === "object" && point.x !== undefined) {
                        const idx = tsToIdx[point.x];
                        if (idx !== undefined) point.x = idx;
                    }
                });
            });
        }

        let title = _.get(structure, "title");
        if (title) {
            title = Object.assign(
                this.HighchartsFormatSeriesAdapter._getText(),
                title
            );

            if (title.style)
                title.style.color =
                    _.get(seriesMapChart, "[0].color") ||
                    this.HighchartsFormatSeriesAdapter._getColor("yellow.dark");
        }

        const options = {
            title: title
                ? title
                : {
                      text: ""
                  },

            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            xAxis: this._getxAxis({
                categories,
                xAxis: structure.xAxis,
                period: filters.period,
                periodDate: filters.date,
                comparation: currentComparation,
                budget
            }),
            yAxis: this.formatYAxis({ series, structure }),
            series: seriesMapChart,
            tooltip: this._getTooltip({ structure, currentComparation, budget })
        };

        const config = { ...structure, ...options };
        const newConfig = config;

        if (newConfig.category === "pie") {
            if (newConfig.legend) {
                delete newConfig.legend.labelFormat;
                newConfig.legend.labelFormatter = this._formatterPieLegend;
            } else {
                newConfig.legend = {
                    align: "right",
                    verticalAlign: "middle",
                    layout: "vertical",
                    labelFormatter: this._formatterPieLegendY
                };
            }
        }

        return newConfig;
    }

    _getTooltip({ structure, currentComparation, budget }) {
        const _formatValue = this.FormatUnits.formatValue;

        if (_.get(structure, "tooltip")) return structure.tooltip;

        const tooltipDefault = {
            backgroundColor: "#f4f4f4",
            style: {
                color: "black",
                font: "9pt Open Sans"
            },
            formatter: function () {
                if (this.series) {
                    const unit = _.get(this.series, "userOptions.unit");

                    const key = _.get(this.series, "userOptions.key");

                    switch (_.get(this.series, "chart.userOptions.category")) {
                        case "waterfall":
                            const percentage =
                                (this.y / _.last(_.get(this.series, "yData"))) *
                                100;

                            const fixed =
                                percentage > 99 || percentage === 0 ? 0 : 1;

                            return `${this.key}: <br>
                        ${_formatValue("currency", this.y)} <br>
                        ${_formatValue("percentage", percentage, fixed)}`;

                        default:
                            let labelDate = "";
                            if (currentComparation.allMonths) {
                                labelDate =
                                    this.point.category +
                                    "/" +
                                    this.point.metadata.year;
                            } else if (budget) {
                                const currentMonth = new Date(
                                    this.point.category
                                ).getUTCMonth();
                                const monthLabel =
                                    DateUtils.monthsAbrev(currentMonth);
                                const isCustomPeriod = [
                                    "yearly",
                                    "quarterly",
                                    "semester"
                                ].includes(currentComparation.period);
                                const prefix = isCustomPeriod
                                    ? this.point.category
                                    : monthLabel;
                                labelDate = `${prefix} ${
                                    isCustomPeriod
                                        ? ""
                                        : `/ ${this.point.metadata.year}`
                                }`;
                            } else labelDate = "";
                            let name = this.series.name;
                            if (this.series?.userOptions?.origin == "goals") {
                                name = `${this.series.name} ${this.series.userOptions.description}`;
                            }

                            const result = `${
                                currentComparation.active || budget
                                    ? this.point.metadata.scenario + " | "
                                    : ""
                            } ${
                                key ? this.key : name
                            } ${labelDate}  ${_formatValue(
                                unit ? unit : _.get(this.point, "unit", "int"),
                                this.y,
                                2
                            )}`;
                            return result;
                    }
                }
            }
        };

        return tooltipDefault;
    }

    _formatterPieLegendY() {
        return `${this.name} (${this.y.toFixed(2)}%)`;
    }

    _formatterPieLegend() {
        const result = (this.y / Math.abs(this.totalPoints)) * 100;

        return `${this.name} (${(result || 0).toFixed(1)}%)`;
    }

    /**
     * @private
     * Verifica se há lacunas de mais de um mês entre datas consecutivas.
     * Quando verdadeiro, o eixo X é trocado para `type: "category"` com
     * espaçamento uniforme, evitando regiões em branco no gráfico.
     *
     * @param {string[]} categories - Array de timestamps numéricos em string (formato "T" do date-fns)
     * @returns {boolean}
     */
    _isSparseCategories(categories) {
        if (!categories || categories.length < 2) return false;
        const threshold = 90 * 24 * 3600 * 1000; // 90 dias em ms (~3 meses) — tolerância para datas com offset de timezone (T03:00:00.000Z)
        for (let i = 1; i < categories.length; i++) {
            if (parseInt(categories[i]) - parseInt(categories[i - 1]) > threshold)
                return true;
        }
        return false;
    }

    /**
     * @private
     *
     * @param {Array} categories
     */
    _getxAxis({ categories, xAxis, period, periodDate, comparation, budget }) {
        if (["yearly", "quarterly", "semester"].includes(period) && !xAxis) {
            categories = categories.map((date) => {
                date = parseInt(date);

                return this.AvailablePeriod.getPeriodLabels(date, period);
            });

            xAxis = {
                categories,
                type: "category",
                uniqueNames: true
            };
        }

        if (comparation.allMonths) {
            return {
                categories: DateUtils.monthsAbrev().map((month, index) => {
                    return `${month}`;
                })
            };
        }
        if (+periodDate === +comparation.date) {
            let axisDate = DateUtils.dateToString(periodDate, "MMM/yyyy");

            if (["yearly", "quarterly", "semester"].includes(period)) {
                let semester = 0;

                if (period === "semester") {
                    const monthCompare = comparation.date.getUTCMonth();
                    semester = (monthCompare + 1) / 6;
                }

                const pattern = DateUtils.labelFormat(period, false, semester);

                axisDate = DateUtils.dateToString(comparation.date, pattern);
            }

            return {
                categories: [axisDate, axisDate]
            };
        }

        if (!xAxis && !comparation.active && this._isSparseCategories(categories)) {
            return {
                type: "category",
                categories: categories.map((date) =>
                    Highcharts.dateFormat("%b'%y", parseInt(date))
                ),
                uniqueNames: false
            };
        }

        xAxis = xAxis
            ? xAxis
            : {
                  type: "datetime",
                  labels: {
                      formatter() {
                          return Highcharts.dateFormat("%b'%y", this.value);
                      }
                  },
                  min: parseInt(_.head(categories)),
                  max: parseInt(_.last(categories)),

                  tickPositioner: comparation.active
                      ? function () {
                            const xData = this.series && this.series[0] && this.series[0].processedXData;
                            return xData || this.tickPositions;
                        }
                      : false
              };

        return xAxis;
    }
}
